import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { safeNext, useAuth } from "../../auth/AuthProvider";
import { EMAIL_RE, ErrorSummary, Field, PasswordField } from "../../components/form";
import { useI18n } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/messages";
import { AuthLayout } from "./AuthLayout";
import { TwoFactorStep } from "../../components/TwoFactorStep";
import { GoogleButton } from "./GoogleButton";

export function LoginPage() {
  const { t } = useI18n();
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Back from a failed Google sign-in (Better Auth appends ?error=<code>).
  const [errors, setErrors] = useState<Record<string, string>>((): Record<string, string> => {
    const code = params.get("error");
    if (!code) return {};
    return { form: code === "coming_soon" ? "auth.errors.comingSoon" : "auth.errors.google" };
  });
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<MessageKey | null>(null);
  const [twoFactor, setTwoFactor] = useState(false);

  if (auth.user) return <Navigate to={next} replace />;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!EMAIL_RE.test(email.trim())) e.email = "auth.errors.email";
    if (!password) e.password = "auth.errors.password";
    return e;
  };

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    setNotice(null);
    const e = validate();
    setErrors(e);
    setAttempt((n) => n + 1);
    if (Object.keys(e).length) return;
    setBusy(true);
    const r = await auth.signIn(email.trim(), password);
    setBusy(false);
    if (r.ok) navigate(next, { replace: true });
    else if ("twoFactor" in r && r.twoFactor) setTwoFactor(true);
    else setErrors({ form: r.error });
  };

  const notVerified = errors.form === "auth.errors.notVerified";

  if (twoFactor) {
    return (
      <AuthLayout title={t("auth.loginTitle")}>
        <TwoFactorStep onDone={() => navigate(next, { replace: true })} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("auth.loginTitle")}>
      <GoogleButton next={next} />
      <form onSubmit={submit} noValidate>
        <ErrorSummary errors={errors} fieldIds={{ email: "login-email", password: "login-password" }} focusKey={attempt} />
        {notVerified && (
          <button
            type="button"
            className="btn btn-link"
            onClick={async () => {
              const r = await auth.resendVerification(email.trim(), next);
              setNotice(r.ok ? "auth.verificationResent" : r.error);
            }}
          >
            {t("auth.resendVerification")}
          </button>
        )}
        {notice && (
          <p className="notice" role="status">
            {t(notice)}
          </p>
        )}
        <Field
          id="login-email"
          label={t("auth.email")}
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          error={errors.email ? t(errors.email as MessageKey) : null}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => email && setErrors((prev) => ({ ...prev, email: EMAIL_RE.test(email.trim()) ? "" : "auth.errors.email" }))}
        />
        <PasswordField
          id="login-password"
          label={t("auth.password")}
          autoComplete="current-password"
          value={password}
          error={errors.password ? t(errors.password as MessageKey) : null}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Link to="/recuperar" className="auth-aside">
          {t("auth.forgotLink")}
        </Link>
        <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={busy}>
          {busy ? t("auth.working") : t("auth.submitLogin")}
        </button>
      </form>
      <p className="auth-switch">
        {t("auth.noAccount")} <Link rel="nofollow" to={`/registro?next=${encodeURIComponent(next)}`}>{t("auth.submitRegister")}</Link>
      </p>
    </AuthLayout>
  );
}
